import type { SoftphoneActionsProps } from './index.types';
import { ReactComponent as KeypadIcon } from 'Assets/icons/keypad_v2.svg';
import { ReactComponent as MergeIcon } from 'Assets/icons/merge_v2.svg';
import { ReactComponent as MuteIcon } from 'Assets/icons/mute_v2.svg';
import { ReactComponent as PauseIcon } from 'Assets/icons/pause_v2.svg';
import { ReactComponent as ResumeIcon } from 'Assets/icons/resume_v2.svg';
import { ReactComponent as TransferIcon } from 'Assets/icons/transfer-call_v2.svg';
import { ReactComponent as UnmuteIcon } from 'Assets/icons/unmute_v2.svg';
import { ReactComponent as VideoIcon } from 'Assets/icons/video_v2.svg';
import {
  TRANSFER_ACTION_NAMES,
  VIDEO_TOOLTIPS,
} from 'Modules/softphone/constants/callActionCopy';
import {
  useSoftphoneCallControls,
  useWarmTransferState,
} from 'Modules/softphone/hooks';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { getUnavailableReason } from 'Modules/softphone/utils/callActionCopy';
import React from 'react';
import { cn } from '~/lib/utils';
import { SoftphoneActionButton } from './SoftphoneActionButton';
import { WarmTransferActions } from './WarmTransferActions';

/**
 * SoftphoneActions - Action buttons grid for the Softphone interface
 *
 * Displays a 3-column grid of action buttons for call control including:
 * - Mute/Unmute microphone
 * - Show/Hide keypad
 * - Hold/Resume call
 * - Start video
 * - Transfer call
 * - Merge calls
 *
 * Uses the useSoftphoneCallControls hook to manage call control states and handlers.
 *
 * @param {SoftphoneActionsProps} props - Component props
 * @returns {React.ReactElement} - Rendered SoftphoneActions component
 */
export const SoftphoneOnCallActions = ({
  className,
  testID,
}: SoftphoneActionsProps): React.ReactElement => {
  const {
    isMuted,
    isOnHold,
    isKeypadActive,
    isVideoActive,
    isTransferActive,
    isMergeActive,
    isMerging,
    permissions,
    isVideoButtonActive,
    videoTooltip,
    handleMute,
    handleHold,
    handleKeypad,
    handleVideo,
    handleTransfer,
    handleMerge,
  } = useSoftphoneCallControls();

  const {
    isConsultationAnswered,
    isCompletingTransfer,
    isWarmTransferConsultationActive,
    handleCompleteWarmTransfer,
    handleCancelWarmTransfer,
  } = useWarmTransferState();

  const isConnectionLost = useSoftphoneStore(
    (state) => state.status === 'disconnected'
  );

  // The call-wide states that give Transfer and Merge a reason of their own for
  // being disabled; the wording (and its precedence) lives with the copy. Both
  // grey out as soon as the consultation starts, but only explain themselves on
  // the handover screen — before that the user hasn't been taken anywhere yet.
  const blockers = {
    isWarmTransferConsultationAnswered: isConsultationAnswered,
    isMerging,
  };

  return (
    <>
      <div
        className={cn(
          'max-w-[202px] mx-auto grid grid-cols-3 gap-x-[24px] gap-y-[16px] mt-[16px] mb-[24px] auto-rows-max justify-items-center',
          className
        )}
        // `group`, not `toolbar`: a toolbar promises arrow-key navigation over
        // a single tab stop, and every button here is tabbed to individually.
        role="group"
        aria-label="Call actions"
        data-testid={testID}
      >
        {/* Mute/Unmute microphone */}
        <SoftphoneActionButton
          icon={isMuted ? UnmuteIcon : MuteIcon}
          action="mute"
          onClick={() => void handleMute()}
          isPressed={isMuted}
          disabled={!permissions.canMute || isConnectionLost}
          testID={testID ? `${testID}-mute` : undefined}
        />

        {/* Show/Hide keypad */}
        <SoftphoneActionButton
          icon={KeypadIcon}
          action="keypad"
          onClick={() => void handleKeypad()}
          isPressed={isKeypadActive}
          disabled={!permissions.canUseKeypad || isConnectionLost}
          testID={testID ? `${testID}-keypad` : undefined}
        />

        {/* Hold/Resume call */}
        <SoftphoneActionButton
          icon={isOnHold ? ResumeIcon : PauseIcon}
          action="hold"
          onClick={() => void handleHold()}
          isPressed={isOnHold}
          disabled={!permissions.canHold || isConnectionLost}
          testID={testID ? `${testID}-hold` : undefined}
        />

        {/* Start video call */}
        <SoftphoneActionButton
          icon={VideoIcon}
          action="video"
          onClick={() => void handleVideo()}
          isPressed={isVideoActive}
          disabled={!isVideoButtonActive || isConnectionLost}
          // "Switch to video" is the offer, not a reason it can't run.
          unavailableReason={
            videoTooltip === VIDEO_TOOLTIPS.enabled ? undefined : videoTooltip
          }
          testID={testID ? `${testID}-video` : undefined}
        />

        {/* Transfer call to another contact */}
        <SoftphoneActionButton
          icon={TransferIcon}
          action="transfer"
          // Names the phase, not the feature: the consultation's Transfer
          // button carries the same caption and the same chord.
          ariaLabel={TRANSFER_ACTION_NAMES.openPanel}
          onClick={() => void handleTransfer()}
          isPressed={isTransferActive}
          disabled={
            !permissions.canTransfer ||
            isWarmTransferConsultationActive ||
            isConnectionLost
          }
          unavailableReason={getUnavailableReason('transfer', blockers)}
          testID={testID ? `${testID}-transfer` : undefined}
        />

        {/* Merge calls into conference */}
        <SoftphoneActionButton
          icon={MergeIcon}
          action="merge"
          onClick={() => void handleMerge()}
          // Pressed inside a conference, but only reads "Merging..." while the
          // merge itself is in flight.
          isPressed={isMergeActive || isMerging}
          isActive={isMerging}
          // Busy, not disabled: `aria-disabled` landing on the button just
          // pressed is read as "Merge unavailable". `handleMerge` guards reentry.
          disabled={
            !permissions.canMerge ||
            isWarmTransferConsultationActive ||
            isConnectionLost
          }
          busy={isMerging}
          unavailableReason={getUnavailableReason('merge', blockers)}
          testID={testID ? `${testID}-merge` : undefined}
        />
      </div>

      {/* Warm transfer actions (third row) - shown during active warm transfer consultation */}
      {isWarmTransferConsultationActive && (
        <WarmTransferActions
          onComplete={() => void handleCompleteWarmTransfer()}
          onCancel={handleCancelWarmTransfer}
          isCompletingTransfer={isCompletingTransfer}
          isConsultationAnswered={isConsultationAnswered}
          testID={testID ? `${testID}-warm-transfer` : undefined}
        />
      )}
    </>
  );
};
